import React, { useState, useEffect } from 'react';
import { ChatMessage, TypingIndicator } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { ProductSidebar } from './ProductSidebar';
import { QuotationPanel } from './QuotationPanel';
import { QuotationResults } from './QuotationResults';
import { LoginScreen } from './LoginScreen';
import { DemoMode } from './DemoMode';
import { useChat } from '@/hooks/useChat';
import { Product } from '@/types';
import {
  MessageCircle,
  Package,
  FileText,
  RefreshCw,
  UserPlus,
  Menu,
  X,
  Play
} from 'lucide-react';
import { clsx } from 'clsx';

export const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<'chat' | 'products' | 'quotation' | 'quotation-results' | 'control'>('chat');
  const [showProductSidebar, setShowProductSidebar] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState<Product[]>([]);
  
  // Quotation results state
  const [quotationResults, setQuotationResults] = useState<any>(null);
  const [selectedPackage, setSelectedPackage] = useState<number>(0);
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [_userData, setUserData] = useState<any>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showDemoMode, setShowDemoMode] = useState(false);
  
  // Quotation settings
  const [showDraftQuotation, setShowDraftQuotation] = useState(true);
  const [emailNotificationEnabled, setEmailNotificationEnabled] = useState(true);
  
  // Function to handle quotation generation success
  const handleQuotationGenerated = (results: any) => {
    setQuotationResults(results);
    setCurrentView('quotation-results');
  };

  const {
    conversation,
    error,
    sendMessage,
    uploadImage,
    startNewConversation: _startNewConversation,
    resetConversation,
    clearError,
    loadDemoConversation,
    getQuotationProgress,
    isQuotationComplete,
    getMissingQuotationFields,
    messagesEndRef,
    isLoading,
  } = useChat({
    autoStart: true,
    onError: (error) => {
      console.error('Chat error:', error);
    },
    onMessageReceived: (response) => {
      // Update featured products when new products are received
      if (response.products && response.products.length > 0) {
        setFeaturedProducts(response.products);
      }
    },
  });

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load quotation settings
  const loadQuotationSettings = () => {
    try {
      const savedSettings = localStorage.getItem('quotationSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        setShowDraftQuotation(settings.show_draft_quotation ?? true);
        setEmailNotificationEnabled(settings.email_notification_enabled ?? true);
      }
    } catch (error) {
      console.error('Error loading quotation settings:', error);
    }
  };

  // Check for existing session on load
  useEffect(() => {
    const token = localStorage.getItem('sessionToken');
    const storedUserData = localStorage.getItem('userData');
    
    if (token && storedUserData) {
      // Validate session with backend
      validateSession(token);
    }
    
    // Load quotation settings
    loadQuotationSettings();
  }, []);
  
  // Listen for settings changes from ControlCenter
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'quotationSettings') {
        loadQuotationSettings();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const validateSession = async (token: string) => {
    try {
      const response = await fetch(`/api/auth/validate-session?session_token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setSessionToken(token);
        setUserData(data.session_data);
        setIsAuthenticated(true);
      } else {
        // Invalid session, clear storage
        localStorage.removeItem('sessionToken');
        localStorage.removeItem('userData');
      }
    } catch (error) {
      console.error('Session validation error:', error);
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('userData');
    }
  };

  const handleLoginSuccess = (token: string, user: any) => {
    setSessionToken(token);
    setUserData(user);
    setIsAuthenticated(true);
    setShowLogin(false);
    setCurrentView('control');
  };

  const _handleLogout = async () => {
    try {
      if (sessionToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ session_token: sessionToken }),
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setSessionToken(null);
      setUserData(null);
      setIsAuthenticated(false);
      setCurrentView('chat');
      localStorage.removeItem('sessionToken');
      localStorage.removeItem('userData');
    }
  };

  const _handleControlCenterAccess = () => {
    if (isAuthenticated) {
      setCurrentView('control');
    } else {
      setShowLogin(true);
    }
  };

  const handleSendMessage = async (message: string, image?: string) => {
    await sendMessage(message, image);
  };

  const handleUploadImage = async (file: File) => {
    await uploadImage(file);
  };

  const handleProductClick = (product: Product) => {
    console.log('Product clicked:', product);
    // Here you could open a product detail modal or add to cart
  };

  const handleNewCustomer = async () => {
    await resetConversation();
  };

  const handleRefresh = async () => {
    // Refresh functionality - could reload products or reset state
    window.location.reload();
  };

  const handleStartDemo = async (scenario: string) => {
    try {
      const response = await fetch(`/api/demo-conversation?scenario=${scenario}`, {
        method: 'POST',
      });
      
      if (response.ok) {
        const data = await response.json();
        // Load the demo conversation into the chat
        loadDemoConversation(data);
        setShowDemoMode(false);
        console.log('Demo started:', data);
      }
    } catch (error) {
      console.error('Error starting demo:', error);
    }
  };

  const handleCompleteDemo = async (conversationId: string) => {
    try {
      const response = await fetch(`/api/demo-complete/${conversationId}`, {
        method: 'POST',
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Demo completed:', data);
        setShowDemoMode(false);
      }
    } catch (error) {
      console.error('Error completing demo:', error);
    }
  };

  const toggleProductSidebar = () => {
    setShowProductSidebar(!showProductSidebar);
  };

  const quotationProgress = getQuotationProgress();
  const missingFields = getMissingQuotationFields();

  // If login screen is active, show it as full screen
  if (showLogin) {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  // Control center and quotation review are now handled by Router component

  return (
    <div className="h-screen bg-gray-50 flex flex-col lg:flex-row">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <MessageCircle size={20} />
              </div>
              <div>
                <h1 className="font-bold text-lg">PrintnGift Sales Assistant</h1>
                <p className="text-sm opacity-90">Mary - Your Corporate Gifts Expert</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Mobile menu button */}
              {isMobile && (
                <button
                  onClick={toggleProductSidebar}
                  className="lg:hidden p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
                >
                  {showProductSidebar ? <X size={20} /> : <Menu size={20} />}
                </button>
              )}

              {/* Action buttons */}
              <button
                onClick={handleNewCustomer}
                className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 px-3 py-2 rounded-lg transition-colors text-sm"
                title="Start new customer session"
              >
                <UserPlus size={16} />
                <span className="hidden sm:inline">New Customer</span>
              </button>

              <button
                onClick={handleRefresh}
                className="flex items-center space-x-2 bg-blue-500 hover:bg-blue-600 px-3 py-2 rounded-lg transition-colors text-sm text-white"
                title="Refresh"
              >
                <RefreshCw size={16} />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              <button
                onClick={() => setShowDemoMode(true)}
                className="flex items-center space-x-2 bg-yellow-500 hover:bg-yellow-600 px-3 py-2 rounded-lg transition-colors text-sm"
                title="Demo Mode"
              >
                <Play size={16} />
                <span className="hidden sm:inline">Demo</span>
              </button>

              {/* Staff buttons removed from customer interface */}
            </div>
          </div>

          {/* Navigation tabs (mobile) */}
          {isMobile && (
            <div className="flex mt-3 space-x-1">
              <button
                onClick={() => setCurrentView('chat')}
                className={clsx(
                  'flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-lg transition-colors text-sm',
                  currentView === 'chat'
                    ? 'bg-white bg-opacity-20'
                    : 'bg-white bg-opacity-10 hover:bg-opacity-15'
                )}
              >
                <MessageCircle size={16} />
                <span>Chat</span>
              </button>
              <button
                onClick={() => setCurrentView('products')}
                className={clsx(
                  'flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-lg transition-colors text-sm',
                  currentView === 'products'
                    ? 'bg-white bg-opacity-20'
                    : 'bg-white bg-opacity-10 hover:bg-opacity-15'
                )}
              >
                <Package size={16} />
                <span>Products</span>
              </button>
              {/* Only show quotation tab if draft quotations are enabled */}
              {showDraftQuotation && (
                <button
                  onClick={() => setCurrentView('quotation')}
                  className={clsx(
                    'flex-1 flex items-center justify-center space-x-2 py-2 px-3 rounded-lg transition-colors text-sm relative',
                    currentView === 'quotation'
                      ? 'bg-white bg-opacity-20'
                      : 'bg-white bg-opacity-10 hover:bg-opacity-15'
                  )}
                >
                  <FileText size={16} />
                  <span>Quote</span>
                  {quotationProgress > 0 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-xs rounded-full flex items-center justify-center">
                      {Math.round(quotationProgress / 100 * 6)}
                    </div>
                  )}
                </button>
              )}
            </div>
          )}
        </header>

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Chat Messages Area */}
          <div className={clsx(
            'flex-1 flex flex-col',
            isMobile && currentView !== 'chat' && 'hidden'
          )}>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <p className="text-red-800">{error}</p>
                    <button
                      onClick={clearError}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}

              {conversation.messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onProductClick={handleProductClick}
                  showProducts={showDraftQuotation}
                />
              ))}

              {isLoading && <TypingIndicator />}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Demo Complete Button */}
            {conversation.id && conversation.id.includes('demo_') && (
              <div className="px-4 py-2 bg-green-50 border-t border-green-200">
                <button
                  onClick={() => handleCompleteDemo(conversation.id!)}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors w-full justify-center"
                >
                  <FileText size={16} />
                  <span>Complete Demo & Generate Full Quotation</span>
                </button>
              </div>
            )}

            {/* Chat Input */}
            <ChatInput
              onSendMessage={handleSendMessage}
              onUploadImage={handleUploadImage}
              isLoading={isLoading}
              placeholder="Type your message here..."
            />
          </div>

          {/* Desktop Product Sidebar */}
          {!isMobile && (
            <div className="w-80 border-l border-gray-200">
              <ProductSidebar
                products={featuredProducts}
                isVisible={true}
                onProductClick={handleProductClick}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile Views */}
      {isMobile && (
        <>
          {/* Mobile Product View */}
          {currentView === 'products' && (
            <div className="fixed inset-0 bg-white z-50 flex flex-col">
              <div className="bg-purple-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-lg">Featured Products</h2>
                  <button
                    onClick={() => setCurrentView('chat')}
                    className="p-2 bg-white bg-opacity-20 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <ProductSidebar
                  products={featuredProducts}
                  isVisible={true}
                  onProductClick={handleProductClick}
                />
              </div>
            </div>
          )}

          {/* Mobile Quotation View - Only show if enabled */}
          {currentView === 'quotation' && showDraftQuotation && (
            <div className="fixed inset-0 bg-white z-50 flex flex-col">
              <div className="bg-purple-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-lg">Quotation Progress</h2>
                  <button
                    onClick={() => setCurrentView('chat')}
                    className="p-2 bg-white bg-opacity-20 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                <QuotationPanel
                  quotationData={conversation.quotationData}
                  progress={quotationProgress}
                  missingFields={missingFields}
                  isComplete={isQuotationComplete()}
                  conversationId={conversation.id}
                  emailNotificationEnabled={emailNotificationEnabled}
                  onQuotationGenerated={handleQuotationGenerated}
                />
              </div>
            </div>
          )}

          {/* Mobile Quotation Results View */}
          {currentView === 'quotation-results' && quotationResults && (
            <div className="fixed inset-0 bg-white z-50 flex flex-col">
              <div className="bg-purple-600 text-white p-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bold text-lg">Quotation Results</h2>
                  <button
                    onClick={() => setCurrentView('chat')}
                    className="p-2 bg-white bg-opacity-20 rounded-lg"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <QuotationResults
                  quotationId={quotationResults.quotation_id}
                  quotationData={quotationResults.quotation_data}
                  packages={quotationResults.package_combinations || []}
                  validUntil={quotationResults.valid_until}
                  generatedAt={quotationResults.generated_at}
                  selectedPackage={selectedPackage}
                  onPackageSelect={setSelectedPackage}
                />
              </div>
            </div>
          )}

          {/* Mobile Product Sidebar Overlay */}
          {showProductSidebar && (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden">
              <div className="absolute right-0 top-0 h-full w-80 bg-white">
                <ProductSidebar
                  products={featuredProducts}
                  isVisible={true}
                  onProductClick={handleProductClick}
                  onClose={() => setShowProductSidebar(false)}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Desktop Quotation Panel - Only show if enabled */}
      {!isMobile && quotationProgress > 0 && showDraftQuotation && (
        <div className="w-64 border-l border-gray-200 bg-white">
          <QuotationPanel
            quotationData={conversation.quotationData}
            progress={quotationProgress}
            missingFields={missingFields}
            isComplete={isQuotationComplete()}
            conversationId={conversation.id}
            emailNotificationEnabled={emailNotificationEnabled}
            onQuotationGenerated={handleQuotationGenerated}
          />
        </div>
      )}

      {/* Demo Mode Modal */}
      <DemoMode
        isVisible={showDemoMode}
        onStartDemo={handleStartDemo}
        onCompleteDemo={handleCompleteDemo}
        onClose={() => setShowDemoMode(false)}
        currentConversationId={conversation.id ?? undefined}
      />
    </div>
  );
};